import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus } from 'lucide-react';
import { SocialIcon } from '../icons/SocialIcon';
// SocialPlatform import might not be strictly needed here anymore unless used for something else
// import type { SocialPlatform } from '../icons/SocialIcon'; 

interface InstagramOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnectViaFacebook: () => void;
  onConnectDirectlyInstagram: () => void;
  isConnecting?: boolean;
  connectingPlatform?: string | null;
}

export const InstagramOptionsModal: React.FC<InstagramOptionsModalProps> = ({
  isOpen,
  onClose,
  onConnectViaFacebook,
  onConnectDirectlyInstagram,
  isConnecting = false,
  connectingPlatform = null,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto"> 
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />

        <div className="relative bg-white rounded-xl border border-black shadow-xl max-w-4xl w-full p-6"> {/* Changed max-w-3xl to max-w-4xl */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <h2 className="text-xl font-semibold">{t('instagramOptionsModal.title')}</h2>
              <SocialIcon platform="instagram" size={24} />
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Changed to grid layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Option 1: Personal Profile (Not Available) */}
            <div className="flex flex-col p-4 rounded-lg border border-gray-300 bg-gray-100 opacity-70 h-full"> {/* Added h-full for consistent height */}
              <div className="flex-grow pt-6">
                <div className="text-3xl mb-3 text-center">👤</div> {/* Emoji - Increased mb-2 to mb-3 */}
                <div className="flex justify-between items-start mb-3"> {/* Title container - Increased mb-2 to mb-3 */}
                  <h3 className="font-semibold text-gray-700 text-center w-full">
                    {t('instagramOptionsModal.option1.titleLine1')}
                    {t('instagramOptionsModal.option1.titleLine2') && <br />}
                    {t('instagramOptionsModal.option1.titleLine2')}
                  </h3>
                </div>
                <p className="text-sm text-gray-500 text-center mb-4">{t('instagramOptionsModal.option1.description')}</p> {/* Description - Increased mb-3 to mb-4 */}
              </div>
              <div className="mt-auto text-center">
                <span className="text-xs font-medium bg-gray-200 text-gray-600 px-2 py-1 rounded-md">{t('instagramOptionsModal.option1.tag')}</span>
              </div>
            </div>

            {/* Option 2: Professional Profile linked to Facebook (Recommended) */}
            <div className="relative flex flex-col p-4 rounded-lg border-2 border-purple-500 bg-purple-50 shadow-sm h-full"> {/* Added relative for absolute positioning of the tag */}
              {/* Recommended Tag - Moved and styled for top-right position */}
              <span 
                className="absolute top-2 right-2 text-xs font-medium bg-purple-100 text-purple-700 px-2 py-1 rounded-md"
              >
                {t('instagramOptionsModal.option2.tag')}
              </span>
              {/* Added pt-6 to this div to create space below the tag and above the emoji/text content */}
              <div className="flex-grow pt-6"> 
                {/* Centered container for logos */}
                <div className="flex justify-center items-center space-x-1 mb-3">
                  <SocialIcon platform="instagram" size={28} />
                  <Plus className="w-4 h-4 text-gray-500" />
                  <SocialIcon platform="facebook" size={28} />
                </div>
                <div className="flex justify-between items-start mb-3"> {/* Title container - Increased mb-2 to mb-3 */}
                  <h3 className="font-semibold text-gray-800 text-center w-full">{t('instagramOptionsModal.option2.title')}</h3>
                </div>
                <p className="text-sm text-gray-600 text-center mb-4">{t('instagramOptionsModal.option2.description')}</p> {/* Description - Increased mb-3 to mb-4 */}
              </div>
              <div className="mt-auto">
                <button
                  onClick={onConnectViaFacebook}
                  disabled={isConnecting}
                  className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 transition duration-150 ease-in-out ${
                    isConnecting 
                      ? 'bg-purple-400 text-white cursor-not-allowed' 
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  {connectingPlatform === 'facebook' ? 'Connecting...' : t('instagramOptionsModal.option2.cta')}
                </button>
              </div>
            </div>

            {/* Option 3: Professional account with no Facebook account */}
            <div className="flex flex-col p-4 rounded-lg border border-gray-300 hover:border-gray-400 h-full"> {/* Added h-full */}
              <div className="flex-grow pt-6"> 
                {/* Centered container for logo */}
                <div className="flex justify-center items-center mb-3">
                  <SocialIcon platform="instagram" size={32} /> {/* Slightly larger as it's solo */}
                </div>
                <div className="flex justify-between items-start mb-3"> {/* Title container - Increased mb-2 to mb-3 */}
                  <h3 className="font-semibold text-gray-700 text-center w-full">{t('instagramOptionsModal.option3.title')}</h3>
                </div>
                <p className="text-sm text-gray-500 text-center mb-4">{t('instagramOptionsModal.option3.description')}</p> {/* Description - Increased mb-3 to mb-4 */}
              </div>
              <div className="mt-auto">
                <button
                  onClick={onConnectDirectlyInstagram}
                  disabled={isConnecting}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 transition duration-150 ease-in-out ${
                    isConnecting 
                      ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed' 
                      : 'bg-white text-purple-600 border-purple-600 hover:bg-purple-50 hover:text-purple-700'
                  }`}
                >
                  {connectingPlatform === 'instagram' ? 'Connecting...' : t('instagramOptionsModal.option3.cta')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper style for lavender background (if not in Tailwind config)
// You might want to add this to your tailwind.config.js or a global CSS file
// For now, let's assume 'bg-lavender-light' is a placeholder for a class like 'bg-purple-50' or similar.
// If 'lavender-light' is not defined, you can use a direct color e.g. style={{ backgroundColor: '#E6E6FA' }}
// or use a tailwind class like bg-purple-100 or bg-indigo-50. Let's use bg-purple-50 for now.
// Replacing 'bg-lavender-light' with 'bg-purple-50' and 'border-primary-500' with 'border-purple-500'
// Replacing 'bg-primary-100 text-primary-700' with 'bg-purple-100 text-purple-700'
// Replacing 'bg-primary-600' with 'bg-purple-600' and 'hover:bg-primary-700' with 'hover:bg-purple-700'
// Replacing 'focus:ring-primary-500' with 'focus:ring-purple-500' 